import { Close, InfoOutlined, ErrorOutline, CheckCircle } from "@mui/icons-material";
import { CircularProgress, Dialog, DialogContent, DialogTitle, IconButton } from "@mui/material";
import moment from "moment";
import { useCallback, useEffect, useState } from "react";
import { toast } from "react-toastify";
import {
  EDITABLE_STATUSES,
  LOSS_TYPE_OPTIONS,
  PAYMENT_MODE_OPTIONS,
  STATUS_APPROVED_COPS_FINANCE,
  STATUS_APPROVED_COPS_INSURANCE,
  isCashPayment,
  resolveApprovalStatus,
} from "./constants";
import {
  mapGhvTransactions,
  computeCollectionDerived,
  buildDiffOnlySavePayload,
  prefillGhvFromPaymentHistory,
  fmtNum,
  isFilled,
  toNumber,
} from "./pureLogic";
import * as api from "./billCollection.service";
import { aws_bucket_url, downloadChatMessage } from "../../../_services";

const TABS = [
  { key: "collectible", label: "Collectible Amount" },
  { key: "extraCash", label: "Extra Cash Proof" },
  { key: "payment", label: "Payment History" },
  { key: "daDocument", label: "DA Document" },
  { key: "gapRemarks", label: "Collection Gap Remarks" },
];

/**
 * Collection Updation — mirrors central_dashboard's `CollectionUpdationComponent`
 * (+ collectible-amount, extra-cash, payment-history, DA, gap-remarks tabs and
 * the Send for Approval modal).
 *
 * `beforeSend` is run right before "Send for Approval" is saved. The parent uses
 * it to commit staged uploads / Approve-Decline decisions first. If it resolves
 * false, Send for Approval is NOT performed.
 */
const CollectionUpdation = ({
  appointmentId,
  leadId,
  collectionStatus,
  paymentMode,
  isInsFBStatus = "",
  // Net Bill Amount from the parent. When present it is the base for the
  // collectible amount (see effectiveCollectableAmount).
  netBillAmount = null,
  // Lens gate — shown above "Collected at Hospital" in the Collectible sub-tab.
  isLensApplicable = false,
  hasLensDecision = false,
  lensChoice = "",
  lensSaved = false,
  lensLocked = false,
  onLensChoice,
  onReopenLensModal,
  beforeSend,
  onStatusChange,
  onApprovalSuccess,
}) => {
  const [loadingMain, setLoadingMain] = useState(true);
  const [collectableAmount, setCollectableAmount] = useState(0);
  const [collectedAtHospital, setCollectedAtHospital] = useState(0);
  const [originalCollectedAtHospital, setOriginalCollectedAtHospital] = useState(0);
  const [originalCollectionLoss, setOriginalCollectionLoss] = useState(0);
  const [originalLossType, setOriginalLossType] = useState("");
  const [ghvTransactions, setGhvTransactions] = useState([]);
  const [originalGhvIds, setOriginalGhvIds] = useState([]);
  const [extraCashRows, setExtraCashRows] = useState([]);
  const [totalAtAdmission, setTotalAtAdmission] = useState(null);
  const [totalAtDischarge, setTotalAtDischarge] = useState(null);
  const [apiPaymentMode, setApiPaymentMode] = useState("");
  const [lossType, setLossType] = useState("");
  const [activeSubTab, setActiveSubTab] = useState("collectible");
  const [paymentHistory, setPaymentHistory] = useState([]);
  const [daDocuments, setDaDocuments] = useState([]);
  const [gapReasons, setGapReasons] = useState([]);
  const [paymentLoaded, setPaymentLoaded] = useState(false);
  const [daLoaded, setDaLoaded] = useState(false);
  const [gapLoaded, setGapLoaded] = useState(false);
  const [saving, setSaving] = useState(false);

  // Send for Approval dialog
  const [approveOpen, setApproveOpen] = useState(false);
  const [snapshot, setSnapshot] = useState(null);
  const [dlgLossType, setDlgLossType] = useState("");
  const [dlgRemarks, setDlgRemarks] = useState("");

  // The status that actually drives the UI. Seeded from the prop, but overridden
  // by the `collectionStatus` returned by get-collection-details — so after a
  // "Send for Approval" save the re-fetch flips this to the new status and the
  // whole form auto-disables (central parity).
  const [effectiveStatus, setEffectiveStatus] = useState(collectionStatus || "");
  useEffect(() => {
    setEffectiveStatus(collectionStatus || "");
  }, [collectionStatus]);

  const effPaymentMode = apiPaymentMode || paymentMode;
  const isCash = isCashPayment(effPaymentMode);

  const isFormDisabled = !!effectiveStatus && !EDITABLE_STATUSES.includes(effectiveStatus);

  // Net Bill Amount wins when present (it is what the backend stores as
  // collectableAmount via uploadFinalBillCentral); else the API's amount.
  // Cash ONLY: for Cashless/Reimbursement the collectible is the extra cash from
  // get-collection-details, never the net bill. Without this guard, switching
  // appointment via search (which prefills netBillAmount from finalBillObj)
  // wrongly showed the net bill as the collectible amount.
  const hasNetBill = isCash && isFilled(netBillAmount) && !isNaN(Number(netBillAmount));
  const effectiveCollectable = hasNetBill ? Number(netBillAmount) : collectableAmount;
  const isCollectibleZero = effectiveCollectable === 0;

  const derived = computeCollectionDerived(
    effectiveCollectable,
    collectedAtHospital,
    ghvTransactions.map((t) => t.amount)
  );
  const { totalGhvCollected, collectionGapRaw, hasExcess, excessAmount } = derived;
  const collectionGap = collectionGapRaw >= 0 ? collectionGapRaw : 0;

  // Inputs lock when the status isn't editable or the lens gate is unresolved.
  const inputsDisabled = isFormDisabled || lensLocked;

  const showPendingNote =
    isCollectibleZero &&
    (effectiveStatus === STATUS_APPROVED_COPS_FINANCE ||
      effectiveStatus === STATUS_APPROVED_COPS_INSURANCE);
  const pendingNoteText =
    effectiveStatus === STATUS_APPROVED_COPS_INSURANCE
      ? "Bill Approve Pending At Insurance."
      : "Bill Approve Pending At Finance.";

  // Fetch collectible details FIRST, then merge success payment-history rows into
  // GHV. Sequential on purpose: fetchDetails overwrites ghvTransactions wholesale,
  // so a parallel merge could be wiped out when the GET resolves last.
  const fetchDetails = useCallback(async () => {
    if (!appointmentId) return;
    setLoadingMain(true);
    try {
      const res = await api.getCollectionDetails(appointmentId);
      const d = res?.data || res || {};
      setCollectableAmount(toNumber(d.collectableAmount));
      const hospital = toNumber(d.collectedAtHospital);
      setCollectedAtHospital(hospital);
      setOriginalCollectedAtHospital(hospital);
      setOriginalCollectionLoss(toNumber(d.collectionLoss));
      setOriginalLossType(d.lossType || "");
      setExtraCashRows(Array.isArray(d.extraCashProof) ? d.extraCashProof : []);
      setTotalAtAdmission(d.totalCollectedAtAdmission ?? null);
      setTotalAtDischarge(d.totalCollectedAtDischarge ?? null);
      setApiPaymentMode(d.paymentMode || "");
      if (d.lossType) setLossType(d.lossType);
      // API status is the source of truth — drives disabling after save.
      if (d.collectionStatus) {
        setEffectiveStatus(d.collectionStatus);
        onStatusChange?.(d.collectionStatus);
      }
      const mappedGhv = mapGhvTransactions(d.collectedAtGHV);
      setOriginalGhvIds(mappedGhv.map((g) => g.id).filter(Boolean));
      try {
        const phRes = await api.getPaymentHistory(appointmentId);
        const phRows = phRes?.result || phRes?.data || [];
        setGhvTransactions(prefillGhvFromPaymentHistory(mappedGhv, phRows));
      } catch {
        setGhvTransactions(mappedGhv);
      }
    } catch (err) {
      console.error(err);
      toast.error("Failed to load collection details");
    } finally {
      setLoadingMain(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [appointmentId]);

  useEffect(() => {
    setPaymentLoaded(false);
    setDaLoaded(false);
    setGapLoaded(false);
    fetchDetails();
  }, [fetchDetails]);

  const loadPaymentHistory = async () => {
    if (paymentLoaded) return;
    try {
      const res = await api.getPaymentHistory(appointmentId);
      setPaymentHistory(res?.result || res?.data || []);
    } catch {
      toast.error("Failed to load payment history");
    } finally {
      setPaymentLoaded(true);
    }
  };

  const loadDaDocs = async () => {
    if (daLoaded || !leadId) return;
    try {
      const res = await api.getDaDocuments(leadId);
      setDaDocuments(res?.data || []);
    } catch {
      toast.error("Failed to load DA documents");
    } finally {
      setDaLoaded(true);
    }
  };

  const loadGapReasons = async () => {
    if (gapLoaded) return;
    try {
      const res = await api.getCollectionGapReason(appointmentId);
      const data = res?.data;
      setGapReasons(Array.isArray(data) ? data : data && data.value ? [data] : []);
    } catch {
      toast.error("Failed to load gap reasons");
    } finally {
      setGapLoaded(true);
    }
  };

  const setTab = (key) => {
    setActiveSubTab(key);
    if (key === "payment") loadPaymentHistory();
    if (key === "daDocument") loadDaDocs();
    if (key === "gapRemarks") loadGapReasons();
  };

  const viewDocument = (docUrl) => {
    if (!docUrl) return;
    const finalUrl = docUrl.startsWith("http") ? docUrl : aws_bucket_url + docUrl;
    downloadChatMessage({ urls: [finalUrl] })
      .then((res) => {
        const { status = "", result = [] } = res || {};
        if (status === "Success" && result.length > 0) window.open(result[0]);
      })
      .catch((err) => console.log("Signed url error", err));
  };

  // ---- GHV rows ----
  const addGhvRow = () =>
    setGhvTransactions((prev) => [
      ...prev,
      { amount: "", paymentMode: "Cash", transactionId: "" },
    ]);

  const updateGhvRow = (index, field, value) =>
    setGhvTransactions((prev) => {
      const copy = [...prev];
      copy[index] = { ...copy[index], [field]: value };
      return copy;
    });

  const removeGhvRow = (index) =>
    setGhvTransactions((prev) => prev.filter((_, i) => i !== index));

  // ---- Send for Approval ----
  const isInsPending =
    typeof isInsFBStatus === "string" && isInsFBStatus.trim().toLowerCase() === "pending";

  // Compute the live Hospital / GHV values FRESH when the dialog opens, so an
  // emptied Hospital field or an added-then-removed GHV row never leaks a stale
  // amount into the gap / collectionLoss calculation.
  const openApprove = () => {
    const hospital = toNumber(collectedAtHospital);
    const ghv = (ghvTransactions || []).reduce((s, t) => s + toNumber(t && t.amount), 0);
    const fresh = computeCollectionDerived(effectiveCollectable, hospital, [ghv]);
    setSnapshot({ collectible: effectiveCollectable, hospital, ghv, ...fresh });
    setDlgLossType(lossType || "");
    setDlgRemarks("");
    setApproveOpen(true);
  };

  const canSubmit =
    !!snapshot &&
    (!snapshot.hasLoss || (!!dlgLossType && !!dlgRemarks && !!dlgRemarks.trim()));

  const persist = async (status, remarks, collectionLoss, lossTypeVal) => {
    setSaving(true);
    // 1) commit staged uploads / decisions (if any), 2) then the normal save.
    let ok = true;
    try {
      ok = beforeSend ? await beforeSend() : true;
    } catch {
      ok = false;
    }
    if (!ok) {
      setSaving(false);
      return;
    }
    try {
      const payload = buildDiffOnlySavePayload(
        {
          appointmentId,
          status,
          remarks: remarks || "",
          lossType: lossTypeVal,
          collectionLoss,
          collectedAtHospital,
          ghvTransactions,
        },
        {
          collectedAtHospital: originalCollectedAtHospital,
          originalGhvIds,
          collectionLoss: originalCollectionLoss,
          lossType: originalLossType,
        }
      );
      await api.saveCollectionEntry(payload);
      toast.success("Collection updated successfully");
      fetchDetails();
      // Only a successful Send for Approval closes the parent.
      onApprovalSuccess?.();
    } catch {
      toast.error("Failed to update collection");
    } finally {
      setSaving(false);
    }
  };

  const submitApprove = () => {
    if (!snapshot) return;
    if (snapshot.hasLoss && !dlgLossType) {
      toast.error("Please select a collection loss type");
      return;
    }
    if (snapshot.hasLoss && !dlgRemarks.trim()) {
      toast.error("Remarks are mandatory");
      return;
    }
    const screenLoss = snapshot.hasLoss ? snapshot.remaining : 0; // gap, or 0
    const screenType = screenLoss > 0 ? dlgLossType : originalLossType; // gap 0: reuse backend type
    const status = resolveApprovalStatus(screenLoss, isCash);
    setApproveOpen(false);
    persist(status, dlgRemarks.trim(), screenLoss, screenType);
  };

  const admissionTotal =
    totalAtAdmission ??
    extraCashRows
      .filter((r) => (r.atTheTimeOf || "").toLowerCase() === "admission")
      .reduce((s, r) => s + toNumber(r.amount), 0);
  const dischargeTotal =
    totalAtDischarge ??
    extraCashRows
      .filter((r) => (r.atTheTimeOf || "").toLowerCase() === "discharge")
      .reduce((s, r) => s + toNumber(r.amount), 0);

  const statusClass = (status, kind) => {
    const s = (status || "").toLowerCase();
    if (kind === "emi") {
      if (s.includes("approv")) return "da-emi-approved";
      if (s.includes("reject") || s.includes("declin")) return "da-emi-rejected";
      return "da-emi-pending";
    }
    if (s.includes("success")) return "ph-status-success";
    if (s.includes("fail")) return "ph-status-fail";
    return "ph-status-pending";
  };

  return (
    <div className="cu-wrap">
      <div className="cu-heading">
        <div>
          <span className="cu-heading-bar" /> COLLECTION UPDATION
        </div>
        {effectiveStatus && (
          <div className="cu-collection-status">
            <span className="cu-status-label">Collection Status:</span>
            <span className="cu-status-badge">{effectiveStatus}</span>
          </div>
        )}
      </div>

      {showPendingNote && (
        <div className="cu-note">
          <InfoOutlined sx={{ fontSize: 15 }} /> {pendingNoteText}
        </div>
      )}

      {/* Sub-tabs */}
      <div className="cu-subtabs">
        {TABS.map(({ key, label }) => (
          <button
            key={key}
            type="button"
            className={`cu-subtab ${activeSubTab === key ? "active" : ""}`}
            onClick={() => setTab(key)}
          >
            {label}
          </button>
        ))}
      </div>

      <div className="cu-body">
        {/* Collectible Amount */}
        {activeSubTab === "collectible" &&
          (loadingMain ? (
            <div className="cu-loading">Loading...</div>
          ) : (
            <div className="ca-wrap">
              {/* Left green summary card */}
              <div className="ca-summary-card">
                <div className="ca-sum-block">
                  <div className="ca-sum-label">COLLECTIBLE AMOUNT</div>
                  <div className="ca-sum-value">&#8377; {fmtNum(effectiveCollectable)}</div>
                </div>
                <div className="ca-sum-block">
                  <div className="ca-sum-label">HOSPITAL</div>
                  <div className="ca-sum-value-sm">&#8377;{fmtNum(collectedAtHospital)}</div>
                </div>
                <div className="ca-sum-block">
                  <div className="ca-sum-label">GHV</div>
                  <div className="ca-sum-value-sm">&#8377;{fmtNum(totalGhvCollected)}</div>
                </div>
                <div className="ca-sum-block ca-gap">
                  <div className="ca-sum-label">COLLECTION GAP</div>
                  <div className="ca-sum-value-sm">&#8377;{fmtNum(collectionGap)}</div>
                </div>
                {lossType && (
                  <div className="ca-sum-block">
                    <div className="ca-sum-label">LOSS TYPE</div>
                    <span className="ca-loss-badge">{lossType}</span>
                  </div>
                )}
                {hasExcess && (
                  <div className="ca-sum-block ca-excess">
                    <div className="ca-sum-label">EXCESS AMOUNT</div>
                    <div className="ca-sum-value-sm ca-excess-value">
                      &#8377;{fmtNum(excessAmount)}
                    </div>
                  </div>
                )}
              </div>

              {/* Right collection entry */}
              <div className="ca-entry">
                {/* Lens gate — only when the final bill flags lens as applicable
                    AND no decision has been persisted yet. Shown ABOVE
                    "Collected at Hospital"; the inputs stay locked until resolved. */}
                {isLensApplicable && !hasLensDecision && (
                  <div className="ca-lens-gate">
                    <div className="ca-lens-note">
                      <InfoOutlined sx={{ fontSize: 16 }} />
                      <span>Lens is required for this hospital</span>
                    </div>
                    <div className="ca-lens-options">
                      <label className="ca-lens-option">
                        <input
                          type="radio"
                          name="caLensChoice"
                          checked={lensChoice === "Applicable"}
                          onChange={() => onLensChoice?.("Applicable")}
                        />
                        <span>Applicable</span>
                      </label>
                      <label className="ca-lens-option">
                        <input
                          type="radio"
                          name="caLensChoice"
                          checked={lensChoice === "Not Applicable"}
                          onChange={() => onLensChoice?.("Not Applicable")}
                        />
                        <span>Not Applicable</span>
                      </label>
                    </div>
                    {lensChoice === "Applicable" && (
                      <div className="ca-lens-status">
                        {lensSaved ? (
                          <>
                            <CheckCircle className="ca-lens-ok" sx={{ fontSize: 15 }} /> Lens
                            details saved.
                          </>
                        ) : (
                          <>
                            <ErrorOutline className="ca-lens-warn" sx={{ fontSize: 15 }} /> Lens
                            details not saved yet.
                            <button
                              type="button"
                              className="ca-lens-reopen"
                              onClick={() => onReopenLensModal?.()}
                            >
                              Enter Lens Details
                            </button>
                          </>
                        )}
                      </div>
                    )}
                  </div>
                )}

                <div className="ca-entry-row">
                  <label className="ca-entry-label">Collected at Hospital:</label>
                  <input
                    type="text"
                    className="cu-input ca-hospital-input"
                    value={collectedAtHospital}
                    onChange={(e) => setCollectedAtHospital(e.target.value)}
                    disabled={inputsDisabled}
                  />
                </div>

                <div className="ca-ghv-head">
                  <span className="ca-ghv-title">Collected at GHV:</span>
                  <button
                    type="button"
                    className="ca-add-btn"
                    onClick={addGhvRow}
                    disabled={inputsDisabled}
                  >
                    + Add Another Transaction
                  </button>
                </div>

                <table className="ca-ghv-table">
                  <thead>
                    <tr>
                      <th>#</th>
                      <th>Amount (Rs.)</th>
                      <th>Payment Mode</th>
                      <th>Transaction ID</th>
                      <th>Action</th>
                    </tr>
                  </thead>
                  <tbody>
                    {ghvTransactions.map((txn, i) => {
                      const rowDisabled = inputsDisabled || txn.isFromPaymentHistory;
                      return (
                        <tr key={i}>
                          <td>{i + 1}</td>
                          <td>
                            <input
                              type="text"
                              className="cu-input"
                              value={txn.amount}
                              onChange={(e) =>
                                updateGhvRow(i, "amount", e.target.value.replace(/[^0-9]/g, ""))
                              }
                              disabled={rowDisabled}
                            />
                          </td>
                          <td>
                            <select
                              className="cu-input"
                              value={txn.paymentMode}
                              onChange={(e) => updateGhvRow(i, "paymentMode", e.target.value)}
                              disabled={rowDisabled}
                            >
                              {txn.paymentMode && !PAYMENT_MODE_OPTIONS.includes(txn.paymentMode) && (
                                <option value={txn.paymentMode}>{txn.paymentMode}</option>
                              )}
                              {PAYMENT_MODE_OPTIONS.map((m) => (
                                <option key={m} value={m}>
                                  {m}
                                </option>
                              ))}
                            </select>
                          </td>
                          <td>
                            <input
                              type="text"
                              className="cu-input"
                              value={txn.transactionId}
                              onChange={(e) => updateGhvRow(i, "transactionId", e.target.value)}
                              disabled={rowDisabled}
                            />
                          </td>
                          <td className="ca-action-cell">
                            <button
                              type="button"
                              className="ca-remove-btn"
                              onClick={() => removeGhvRow(i)}
                              title="Remove"
                              disabled={rowDisabled}
                            >
                              &#10006;
                            </button>
                          </td>
                        </tr>
                      );
                    })}
                    {!ghvTransactions.length && (
                      <tr>
                        <td colSpan={5} className="ca-empty">
                          No GHV transactions added.
                        </td>
                      </tr>
                    )}
                  </tbody>
                </table>

                <div className="ca-ghv-total">
                  Total GHV Collected: &#8377; {fmtNum(totalGhvCollected)}
                </div>
              </div>
            </div>
          ))}

        {/* Extra Cash Proof */}
        {activeSubTab === "extraCash" && (
          <div className="ecp-wrap">
            <div className="ecp-head">
              <span className="ecp-title">Extra Cash Proof</span>
              <span className="ecp-totals">
                Total Collected At Admission: <b>{fmtNum(admissionTotal)}</b>
                &nbsp;&nbsp; Total Collected At Discharge: <b>{fmtNum(dischargeTotal)}</b>
              </span>
            </div>
            <div className="bcm-table-wrap">
              <table className="ecp-table">
                <thead>
                  <tr>
                    <th>File</th>
                    <th>Amount</th>
                    <th>Collected By</th>
                    <th>Collected At</th>
                    <th>At The Time Of</th>
                    <th>Source</th>
                  </tr>
                </thead>
                <tbody>
                  {extraCashRows.map((row, i) => (
                    <tr key={i}>
                      <td>
                        <button
                          type="button"
                          className="ecp-file"
                          onClick={() => viewDocument(row.url)}
                          disabled={!row.url}
                        >
                          {row.url ? "View File" : "-"}
                        </button>
                      </td>
                      <td>&#8377;{fmtNum(row.amount)}</td>
                      <td>{row.collectedBy || "-"}</td>
                      <td>{row.collectedAt || "-"}</td>
                      <td>
                        <span className="ecp-pill">{row.atTheTimeOf || "-"}</span>
                      </td>
                      <td>{row.source || "-"}</td>
                    </tr>
                  ))}
                  {!extraCashRows.length && (
                    <tr>
                      <td colSpan={6} className="ecp-empty">
                        No extra cash proofs found.
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          </div>
        )}

        {/* Payment History */}
        {activeSubTab === "payment" &&
          (!paymentLoaded ? (
            <div className="cu-loading">Loading...</div>
          ) : (
            <div className="ph-wrap">
              <div className="bcm-table-wrap">
                <table className="ph-table">
                  <thead>
                    <tr>
                      <th>Date &amp; Time of Requested Payment</th>
                      <th>Status</th>
                      <th>Transaction ID</th>
                      <th>Amount(&#8377;)</th>
                      <th>Approver</th>
                    </tr>
                  </thead>
                  <tbody>
                    {paymentHistory.map((row) => (
                      <tr key={row._id}>
                        <td>
                          <div>
                            {row._created_at ? moment(row._created_at).format("DD-MM-YY HH:mm") : "-"}
                          </div>
                          {!row.appointmentId && (
                            <div className="ph-sub">(Payment not connected with any IPD)</div>
                          )}
                        </td>
                        <td>
                          <span className={`ph-status ${statusClass(row.status, "payment")}`}>
                            {row.status || "-"}
                          </span>
                        </td>
                        <td>
                          <button
                            type="button"
                            className="ph-link"
                            onClick={() =>
                              row.paymentLink && window.open(row.paymentLink, "_blank")
                            }
                          >
                            {row.invoiceNumber || row._id || "-"}
                          </button>
                        </td>
                        <td>{fmtNum(row.amount)}</td>
                        <td>{row.mappedByUserName || "N/A"}</td>
                      </tr>
                    ))}
                    {!paymentHistory.length && (
                      <tr>
                        <td colSpan={5} className="ph-empty">
                          No payment history found.
                        </td>
                      </tr>
                    )}
                  </tbody>
                </table>
              </div>
            </div>
          ))}

        {/* DA Document */}
        {activeSubTab === "daDocument" &&
          (!daLoaded ? (
            <div className="cu-loading">Loading...</div>
          ) : (
            <div className="da-wrap">
              <div className="da-title">DA Document</div>
              <div className="bcm-table-wrap">
                <table className="da-table">
                  <thead>
                    <tr>
                      <th>DA Document</th>
                      <th>Date</th>
                      <th>EMI Status</th>
                      <th>Loan Amount</th>
                      <th>Vendor</th>
                      <th>Loan Id</th>
                    </tr>
                  </thead>
                  <tbody>
                    {daDocuments.map((row, i) => (
                      <tr key={i}>
                        <td>
                          <button
                            type="button"
                            className="da-link"
                            onClick={() => viewDocument(row.daDocument)}
                          >
                            Open Document
                          </button>
                        </td>
                        <td>{row.date || "-"}</td>
                        <td>
                          <span className={`da-emi ${statusClass(row.emiStatus, "emi")}`}>
                            &#9679; {row.emiStatus || "-"}
                          </span>
                        </td>
                        <td>{row.loanAmount ? `\u20B9${fmtNum(row.loanAmount)}` : "-"}</td>
                        <td>{row.vendor || "-"}</td>
                        <td>{row.loanId || row.loanID || "-"}</td>
                      </tr>
                    ))}
                    {!daDocuments.length && (
                      <tr>
                        <td colSpan={6} className="da-empty">
                          No DA documents found.
                        </td>
                      </tr>
                    )}
                  </tbody>
                </table>
              </div>
            </div>
          ))}

        {/* Collection Gap Remarks */}
        {activeSubTab === "gapRemarks" &&
          (!gapLoaded ? (
            <div className="cu-loading">Loading...</div>
          ) : (
            <div className="gr-wrap">
              <div className="gr-title">Collection Gap Remarks</div>
              {gapReasons.length ? (
                <div className="bcm-table-wrap">
                  <table className="gr-table">
                    <thead>
                      <tr>
                        <th>Gap Remarks</th>
                        <th>Date &amp; Time</th>
                        <th>Added by</th>
                      </tr>
                    </thead>
                    <tbody>
                      {gapReasons.map((r, i) => (
                        <tr key={i}>
                          <td>{r.value}</td>
                          <td>
                            {r.dateTimeIso ? moment(r.dateTimeIso).format("DD MMM YYYY, h:mma") : "-"}
                          </td>
                          <td>{r.actionTakenByUser || "-"}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              ) : (
                <div className="gr-empty">No collection gap remarks found.</div>
              )}
            </div>
          ))}
      </div>

      {/* Actions */}
      <div className="cu-actions">
        <button
          type="button"
          className="cu-approve"
          disabled={saving || isFormDisabled}
          onClick={openApprove}
        >
          {saving ? <CircularProgress size={16} sx={{ color: "#fff" }} /> : "Send for Approval"}
        </button>
      </div>

      {/* Send for Approval dialog (central CollectionApproveModalComponent) */}
      <Dialog
        open={approveOpen}
        onClose={() => setApproveOpen(false)}
        maxWidth="xs"
        fullWidth
      >
        <DialogTitle
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            borderBottom: "1px solid #eef0f4",
          }}
        >
          <span className="ca-modal-title">Send for Approval</span>
          <IconButton onClick={() => setApproveOpen(false)} size="small">
            <Close />
          </IconButton>
        </DialogTitle>
        <DialogContent sx={{ pt: "20px !important", px: "24px" }}>
          {snapshot && (
            <>
              {snapshot.hasLoss && (
                <p className="ca-note">
                  The collected amount does not match the collectible. Mark the collection gap
                  as a loss to proceed.
                </p>
              )}
              {!snapshot.hasLoss && !snapshot.hasExcess && (
                <p className="ca-note">
                  Collections reconcile exactly. You can send this collection for approval.
                </p>
              )}
              {snapshot.hasExcess && (
                <p className="ca-note">
                  Collected amount exceeds collectible. Excess amount will be recorded.
                </p>
              )}

              <div className="ca-summary">
                <div className="ca-row">
                  <span>Collectible Amount</span>
                  <span>&#8377; {fmtNum(snapshot.collectible)}</span>
                </div>
                <div className="ca-row">
                  <span>Collected at Hospital</span>
                  <span>&#8377; {fmtNum(snapshot.hospital)}</span>
                </div>
                <div className="ca-row">
                  <span>Collected at GHV</span>
                  <span>&#8377; {fmtNum(snapshot.ghv)}</span>
                </div>
                <div className="ca-row ca-row-total">
                  <span>Collection Gap (Loss)</span>
                  <span>&#8377; {fmtNum(snapshot.remaining)}</span>
                </div>
                {lossType && (
                  <div className="ca-row ca-row-sub">
                    <span className="ca-loss-label">Loss Type</span>
                    <span className="ca-loss-badge">{lossType}</span>
                  </div>
                )}
                {isInsPending && (
                  <div className="ca-row ca-row-sub">
                    <span className="ca-loss-label">Insurance FB Status</span>
                    <span
                      className="ca-loss-badge"
                      style={{ background: "#fff3cd", color: "#856404", border: "1px solid #ffeeba" }}
                    >
                      Pending
                    </span>
                  </div>
                )}
                {snapshot.hasExcess && (
                  <div className="ca-row ca-row-excess">
                    <span>Excess Amount</span>
                    <span className="ca-excess-value">&#8377; {fmtNum(snapshot.excessAmount)}</span>
                  </div>
                )}
              </div>

              {snapshot.hasLoss && (
                <div className="ca-field">
                  <label className="ca-label">
                    MARK &#8377; {fmtNum(snapshot.remaining)} AS COLLECTION LOSS?
                  </label>
                  <label className="ca-label">
                    Collection Loss Type <span className="req">*</span>
                  </label>
                  <select
                    className="cu-input"
                    value={dlgLossType}
                    onChange={(e) => setDlgLossType(e.target.value)}
                  >
                    <option value="">-- Select Loss Type --</option>
                    {LOSS_TYPE_OPTIONS.map((opt) => (
                      <option key={opt} value={opt}>
                        {opt}
                      </option>
                    ))}
                  </select>
                </div>
              )}

              <div className="ca-field">
                <label className="ca-label">
                  Remarks {snapshot.hasLoss && <span className="req">*</span>}
                </label>
                <textarea
                  className="cu-input"
                  rows={3}
                  value={dlgRemarks}
                  onChange={(e) => setDlgRemarks(e.target.value)}
                  placeholder={
                    snapshot.hasLoss
                      ? "Enter reason for the collection loss."
                      : "Enter remarks (optional)."
                  }
                  style={{ resize: "vertical", minHeight: 80, height: "auto" }}
                />
              </div>

              <div className="ca-footer">
                <button
                  type="button"
                  className="ca-submit"
                  disabled={!canSubmit}
                  onClick={submitApprove}
                >
                  Send for Approval &#10003;
                </button>
              </div>
            </>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default CollectionUpdation;
